"use client";

import Link from "next/link";
import { ArrowRight, BriefcaseBusiness, GraduationCap } from "lucide-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
	useEffect,
	useRef,
	useState,
	type CSSProperties,
	type ReactNode,
} from "react";
import CertificationCard from "@/components/site/certification-card";
import type { PublicCertificationItem } from "@/lib/services/certification.service";
import type { PublicExperienceItem } from "@/lib/services/experience.service";
import { parseExperiencePeriod } from "@/lib/experience-period";

function splitExperienceDescription(description: string | null) {
	if (!description) return { intro: [] as string[], bullets: [] as string[] };

	const lines = description
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean);

	if (lines.length <= 1) {
		return { intro: lines, bullets: [] as string[] };
	}

	const bullets = lines
		.filter((line) => line.startsWith("-") || line.startsWith("•"))
		.map((line) => line.replace(/^[-•]\s*/, "").trim());

	const intro = lines.filter(
		(line) => !line.startsWith("-") && !line.startsWith("•"),
	);
	return { intro, bullets };
}

function getCompanyInitials(name: string) {
	const letters = name
		.split(" ")
		.map((part) => part.trim())
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");

	return letters || "CO";
}

const COMPANY_LOGO_BY_KEYWORD: Array<{
	keywords: string[];
	domain: string;
}> = [
	{
		keywords: ["bangkit academy"],
		domain: "bangkit.academy",
	},
	{
		keywords: ["google"],
		domain: "google.com",
	},
	{
		keywords: ["tokopedia"],
		domain: "tokopedia.com",
	},
	{
		keywords: ["gojek"],
		domain: "gojek.com",
	},
	{
		keywords: ["traveloka"],
		domain: "traveloka.com",
	},
	{
		keywords: ["dimension"],
		domain: "dimension.dev",
	},
	{
		keywords: ["apple developer academy"],
		domain: "developer.apple.com",
	},
];

const EDUCATION_ITEMS = [
	{
		id: "stti-tanjung-pinang",
		institution: "SEKOLAH TINGGI TEKNOLOGI INDONESIA TANJUNG PINANG",
		degree: "Bachelor of Computer Science",
		period: "Jul 2019 - Nov 2023",
		gpa: "3.91",
	},
	{
		id: "sma-negeri-2-tanjungpinang",
		institution: "SMA NEGERI 2 TANJUNGPINANG",
		degree: "Mathematics and Natural Sciences",
		period: "2015 - 2018",
		gpa: null,
	},
] as const;

function getCompanyLogoUrl(organization: string) {
	const normalized = organization.toLowerCase();
	const matched = COMPANY_LOGO_BY_KEYWORD.find((item) =>
		item.keywords.some((keyword) => normalized.includes(keyword)),
	);

	if (!matched) return null;

	return `https://www.google.com/s2/favicons?domain=${matched.domain}&sz=64`;
}

const MONTH_LABELS_UPPER = [
	"JAN",
	"FEB",
	"MAR",
	"APR",
	"MAY",
	"JUN",
	"JUL",
	"AUG",
	"SEP",
	"OCT",
	"NOV",
	"DEC",
] as const;

function monthInputToUpperLabel(value: string) {
	const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
	if (!match) return null;
	const year = match[1];
	const monthIndex = Number(match[2]) - 1;
	return `${MONTH_LABELS_UPPER[monthIndex]} ${year}`;
}

function monthInputToMonthNumber(value: string) {
	const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
	if (!match) return null;
	const year = Number(match[1]);
	const monthIndex = Number(match[2]) - 1;
	return year * 12 + monthIndex;
}

function formatDurationLabel(totalMonths: number) {
	const safeMonths = Math.max(totalMonths, 0);
	const years = Math.floor(safeMonths / 12);
	const months = safeMonths % 12;
	const parts: string[] = [];

	if (years > 0) {
		parts.push(`${years} yrs`);
	}
	if (months > 0 || parts.length === 0) {
		parts.push(`${months} mos`);
	}

	return parts.join(" ");
}

function buildHoverPeriod(period: string) {
	const parsed = parseExperiencePeriod(period);
	if (!parsed) return null;

	const startValue = monthInputToMonthNumber(parsed.startMonth);
	const startLabel = monthInputToUpperLabel(parsed.startMonth);
	if (startValue === null || !startLabel) return null;

	const endSource = parsed.isCurrent
		? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`
		: parsed.endMonth;
	const endValue = monthInputToMonthNumber(endSource);
	if (endValue === null) return null;

	const durationLabel = formatDurationLabel(endValue - startValue);

	const durationWidthCh = Math.max(durationLabel.length * 0.82 + 0.8, 4.8);

	if (parsed.isCurrent) {
		return { startLabel, endLabel: "PRESENT", durationLabel, durationWidthCh };
	}

	const endLabel = monthInputToUpperLabel(parsed.endMonth);
	if (!endLabel) return null;

	return { startLabel, endLabel, durationLabel, durationWidthCh };
}

function SequenceReveal({
	children,
	started,
	delay = 0,
	duration = 0.9,
	distance = 44,
	className,
}: {
	children: ReactNode;
	started: boolean;
	delay?: number;
	duration?: number;
	distance?: number;
	className?: string;
}) {
	const shouldReduceMotion = useReducedMotion();

	return (
		<motion.div
			className={className}
			initial={{
				opacity: 0,
				y: shouldReduceMotion ? 0 : distance,
				scale: shouldReduceMotion ? 1 : 0.985,
				filter: shouldReduceMotion ? "blur(0px)" : "blur(8px)",
			}}
			animate={
				started
					? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
					: undefined
			}
			transition={{
				duration: shouldReduceMotion ? 0.25 : duration,
				delay: shouldReduceMotion ? 0 : delay,
				ease: [0.22, 1, 0.36, 1],
			}}
		>
			{children}
		</motion.div>
	);
}

export default function AboutCareerJourney({
	experiences,
	certifications,
}: {
	experiences: PublicExperienceItem[];
	certifications: PublicCertificationItem[];
}) {
	const sectionRef = useRef<HTMLDivElement | null>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.08 });
	const [started, setStarted] = useState(false);

	useEffect(() => {
		if (isInView) {
			setStarted(true);
		}
	}, [isInView]);

	const previewCertifications = certifications.slice(0, 3);
	const hasMoreCertifications =
		certifications.length > previewCertifications.length;
	const inHouseExperiences = experiences.filter(
		(item) => item.experienceType === "IN_HOUSE",
	);
	const vendorExperiences = experiences.filter(
		(item) => item.experienceType === "VENDOR_CONTRACT",
	);
	const educationHeaderDelay = 0.06;
	const educationBaseDelay = 0.22;
	const educationStep = 0.18;
	const experiencesHeaderDelay =
		educationBaseDelay + EDUCATION_ITEMS.length * educationStep + 0.18;
	const experienceBaseDelay = experiencesHeaderDelay + 0.16;
	const experienceStep = 0.22;
	const certificationsHeaderDelay =
		experienceBaseDelay + experiences.length * experienceStep + 0.16;
	const certificationBaseDelay = certificationsHeaderDelay + 0.14;
	const certificationStep = 0.18;

	const renderExperienceItems = (
		items: PublicExperienceItem[],
		startIndex: number,
	) =>
		items.map((experience, index) => {
			const { intro, bullets } = splitExperienceDescription(
				experience.description,
			);
			const companyInitials = getCompanyInitials(experience.organization);
			const companyGoogleUrl = `https://www.google.com/search?q=${encodeURIComponent(
				`${experience.organization} ${experience.location}`.trim(),
			)}`;
			const companyLogoUrl = getCompanyLogoUrl(experience.organization);
			const resolvedCompanyIconUrl =
				experience.iconUrl && experience.iconUrl.trim()
					? experience.iconUrl
					: companyLogoUrl;
			const hoverPeriod = buildHoverPeriod(experience.period);
			const articleDelay =
				experienceBaseDelay + (startIndex + index) * experienceStep;

			return (
				<SequenceReveal
					key={experience.id}
					started={started}
					delay={articleDelay}
					duration={0.95}
					distance={52}
				>
					<article className="grid gap-5 border-b border-white/10 pb-12 md:grid-cols-[minmax(190px,220px)_1fr] lg:grid-cols-[minmax(210px,240px)_1fr]">
						<div className="relative text-[0.72rem] font-medium uppercase tracking-[0.09em] text-zinc-500 md:pt-1">
							{hoverPeriod ? (
								<>
									<span className="block w-fit whitespace-nowrap md:hidden">
										{experience.period}
									</span>
									<span className="group/date hidden w-fit items-start whitespace-nowrap md:flex md:group-hover/date:text-zinc-400">
										<span className="shrink-0">{hoverPeriod.startLabel}</span>
										<span
											style={
												{
													"--duration-width-ch": `${hoverPeriod.durationWidthCh}ch`,
												} as CSSProperties
											}
											className="relative mx-1.5 inline-flex h-[1.4em] w-[1.1ch] items-center overflow-hidden leading-[1.2] transition-[width] duration-300 ease-out group-hover/date:w-[var(--duration-width-ch)]"
										>
											<span className="absolute inset-0 transition-all duration-300 ease-out group-hover/date:-translate-y-full group-hover/date:opacity-0">
												—
											</span>
											<span className="absolute inset-0 inline-block whitespace-nowrap text-center translate-y-full text-[#dfe5ef] normal-case tracking-[0.02em] opacity-0 transition-all duration-300 ease-out group-hover/date:translate-y-0 group-hover/date:opacity-100">
												{hoverPeriod.durationLabel}
											</span>
										</span>
										<span className="shrink-0">{hoverPeriod.endLabel}</span>
									</span>
								</>
							) : (
								<span className="block w-fit whitespace-nowrap">
									{experience.period}
								</span>
							)}
						</div>
						<div className="space-y-4">
							<SequenceReveal
								started={started}
								delay={articleDelay + 0.06}
								duration={0.82}
								distance={26}
							>
								<div>
									<h3 className="font-rethink text-[1.48rem] leading-[1.14] font-semibold text-white">
										{experience.title}
									</h3>
									<div className="mt-1.5 flex flex-wrap items-center gap-2 text-[0.96rem] text-zinc-400">
										<a
											href={companyGoogleUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="group/link inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-2 text-zinc-300 transition hover:text-white"
										>
											{resolvedCompanyIconUrl ? (
												<img
													src={resolvedCompanyIconUrl}
													alt={`${experience.organization} logo`}
													className="h-5 w-5 rounded-md bg-white/95 p-[1px]"
												/>
											) : (
												<span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-linear-to-br from-pink-300 via-orange-200 to-purple-300 text-[10px] font-semibold text-black">
													{companyInitials}
												</span>
											)}
											<span className="underline decoration-white/35 underline-offset-2 transition-[text-decoration-color] duration-200 group-hover/link:decoration-white">
												{experience.organization}
											</span>
										</a>
										<span>-</span>
										<span>{experience.location}</span>
									</div>
								</div>
							</SequenceReveal>
							<SequenceReveal
								started={started}
								delay={articleDelay + 0.14}
								duration={0.9}
								distance={26}
							>
								<div className="space-y-2.5 text-[0.89rem] leading-[1.62] text-zinc-400">
									{intro.map((line, introIndex) => (
										<p key={`${experience.id}-intro-${introIndex}`}>{line}</p>
									))}
									{bullets.length > 0 ? (
										<ul className="list-disc space-y-1.5 pl-5 text-zinc-400 marker:text-zinc-600">
											{bullets.map((bullet, bulletIndex) => (
												<li key={`${experience.id}-bullet-${bulletIndex}`}>
													{bullet}
												</li>
											))}
										</ul>
									) : null}
								</div>
							</SequenceReveal>
						</div>
					</article>
				</SequenceReveal>
			);
		});

	return (
		<div ref={sectionRef}>
			<section className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
				<SequenceReveal
					started={started}
					delay={educationHeaderDelay}
					duration={0.95}
				>
					<div className="mb-7 space-y-2">
						<div className="mb-4 inline-flex rounded-xl border border-[#cfd5df]/20 bg-[linear-gradient(160deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-2 text-zinc-200">
							<GraduationCap className="h-5 w-5" />
						</div>
						<h2 className="font-rethink text-2xl font-semibold text-white sm:text-3xl">
							Education
						</h2>
						<p className="max-w-2xl text-sm leading-relaxed text-zinc-400">
							Academic background that shaped my foundation in computer science
							and software development.
						</p>
					</div>
				</SequenceReveal>
				<div className="space-y-14">
					{EDUCATION_ITEMS.map((education, index) => {
						const articleDelay = educationBaseDelay + index * educationStep;

						return (
							<SequenceReveal
								key={education.id}
								started={started}
								delay={articleDelay}
								duration={0.95}
								distance={52}
							>
								<article className="grid gap-5 border-b border-white/10 pb-12 md:grid-cols-[minmax(190px,220px)_1fr] lg:grid-cols-[minmax(210px,240px)_1fr]">
									<div className="relative text-[0.72rem] font-medium uppercase tracking-[0.09em] text-zinc-500 md:pt-1">
										<span className="block w-fit whitespace-nowrap">
											{education.period}
										</span>
									</div>
									<div className="space-y-4">
										<SequenceReveal
											started={started}
											delay={articleDelay + 0.06}
											duration={0.82}
											distance={26}
										>
											<div>
												<h3 className="font-rethink text-[1.48rem] leading-[1.14] font-semibold text-white">
													{education.institution}
												</h3>
												<div className="mt-1.5 flex flex-wrap items-center gap-2 text-[0.96rem] text-zinc-400">
													<span>{education.degree}</span>
													{education.gpa ? (
														<>
															<span>-</span>
															<span>GPA: {education.gpa}</span>
														</>
													) : null}
												</div>
											</div>
										</SequenceReveal>
									</div>
								</article>
							</SequenceReveal>
						);
					})}
				</div>
			</section>

			<section className="mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6">
				<SequenceReveal
					started={started}
					delay={experiencesHeaderDelay}
					duration={0.95}
				>
					<div className="mb-12 text-center">
						<div className="mb-4 inline-flex rounded-xl border border-[#cfd5df]/20 bg-[linear-gradient(160deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-2 text-zinc-200">
							<BriefcaseBusiness className="h-5 w-5" />
						</div>
						<h2 className="font-rethink text-3xl font-semibold text-white">
							Experiences
						</h2>
					</div>
				</SequenceReveal>
				{experiences.length === 0 ? (
					<p className="text-sm text-zinc-500">
						No public experience data available yet.
					</p>
				) : (
					<div className="space-y-12">
						{inHouseExperiences.length > 0 ? (
							<div className="space-y-8">
								<div className="space-y-14">
									{renderExperienceItems(inHouseExperiences, 0)}
								</div>
							</div>
						) : null}
						{vendorExperiences.length > 0 ? (
							<div className="space-y-8">
								<div className="max-w-4xl">
									<h3 className="font-rethink text-4xl font-semibold leading-tight text-white sm:text-5xl">
										What I&apos;ve{" "}
										<span className="text-zinc-500">Built Along the Way</span>
									</h3>
									<div className="mt-3 h-px w-40 bg-white/10 sm:w-52" />
									<p className="mt-3 text-sm text-zinc-400">
										Selected vendor and contract engagements across products and
										platforms.
									</p>
								</div>
								<div className="space-y-14">
									{renderExperienceItems(
										vendorExperiences,
										inHouseExperiences.length,
									)}
								</div>
							</div>
						) : null}
					</div>
				)}
			</section>

			<section className="mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6">
				<SequenceReveal
					started={started}
					delay={certificationsHeaderDelay}
					duration={0.95}
				>
					<div className="mb-7 space-y-2">
						<h2 className="font-rethink text-2xl font-semibold text-white sm:text-3xl">
							Learning Tracks
						</h2>
						<p className="max-w-2xl text-sm leading-relaxed text-zinc-400">
							Credentials that reflect my focus on cloud, software engineering,
							and continuous learning.
						</p>
					</div>
				</SequenceReveal>
				{certifications.length === 0 ? (
					<p className="text-sm text-zinc-500">
						No public certification data available yet.
					</p>
				) : (
					<div className="space-y-4">
						{previewCertifications.map((certification, index) => {
							const articleDelay =
								certificationBaseDelay + index * certificationStep;

							return (
								<SequenceReveal
									key={certification.id}
									started={started}
									delay={articleDelay}
									duration={0.95}
									distance={52}
								>
									<CertificationCard
										certification={certification}
										index={index}
									/>
								</SequenceReveal>
							);
						})}
						{hasMoreCertifications ? (
							<SequenceReveal
								started={started}
								delay={
									certificationBaseDelay +
									previewCertifications.length * certificationStep +
									0.1
								}
								duration={0.9}
								distance={28}
								className="flex justify-center pt-2 sm:pt-3"
							>
								<Link
									href="/certifications"
									className="group inline-flex items-center gap-2.5 text-[0.92rem] leading-none font-medium tracking-[-0.02em] text-zinc-300 transition hover:text-white sm:text-[1rem]"
								>
									<span>See more certifications</span>
									<span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/12 bg-white/[0.025] text-zinc-300 transition duration-300 group-hover:border-white/24 group-hover:bg-white/[0.05] group-hover:text-white">
										<ArrowRight className="h-3 w-3 transition duration-300 group-hover:translate-x-0.5" />
									</span>
								</Link>
							</SequenceReveal>
						) : null}
					</div>
				)}
			</section>
		</div>
	);
}
